// Panel 1 — Ranking de líderes del turno (mockup "Ranking de líderes").

import type { PantallaDatos } from 'schemas';
import {
  Barra,
  colorPct,
  Corona,
  ETIQUETA,
  fmt,
  nombreCorto,
  PanelVacio,
  TituloPanel,
} from '../shared';

const MAX_FILAS = 12;

export function PanelLideres({ datos, baseLabel }: { datos: PantallaDatos; baseLabel: string }) {
  const lideres = datos.lideres.slice(0, MAX_FILAS);
  const restantes = datos.lideres.length - lideres.length;

  return (
    <section className="flex h-full flex-col gap-6 px-[4vw] py-[3vh]">
      <TituloPanel
        titulo="Ranking de líderes"
        subtitulo={`Cumplimiento del turno · producido validado vs ${baseLabel}`}
      />
      {lideres.length === 0 ? (
        <PanelVacio mensaje="Sin capturas en el turno todavía" />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col justify-start">
          <div
            className={`grid grid-cols-[2.2rem_minmax(0,1fr)_5.5rem_5.5rem_minmax(4rem,10rem)_4rem] items-center gap-x-3 px-5 pb-2 ${ETIQUETA} tracking-[0.1em] text-fg-tertiary lg:grid-cols-[3.2rem_minmax(0,1fr)_7rem_7rem_11rem_5.5rem] lg:gap-x-4`}
          >
            <span>#</span>
            <span>LÍDER</span>
            <span className="text-right">PRODUCIDO</span>
            <span className="text-right">ESPERADO</span>
            <span />
            <span className="text-right">CUMPL.</span>
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {lideres.map((l, i) => {
              const primero = i === 0;
              const enRiesgo = (l.cumplimiento_pct ?? 100) < 70;
              return (
                <div
                  key={l.user_id}
                  className={`grid grid-cols-[2.2rem_minmax(0,1fr)_5.5rem_5.5rem_minmax(4rem,10rem)_4rem] items-center gap-x-3 rounded-lg px-5 lg:grid-cols-[3.2rem_minmax(0,1fr)_7rem_7rem_11rem_5.5rem] lg:gap-x-4 ${
                    primero ? 'border border-border-strong bg-surface-elevated' : ''
                  }`}
                  style={{
                    paddingBlock: 'clamp(0.35rem, 0.9vh, 0.8rem)',
                    background: !primero && enRiesgo ? 'var(--crit-soft)' : undefined,
                  }}
                >
                  <span className="text-fg-tertiary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {primero ? (
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border-strong text-fg">
                        <Corona size={17} />
                      </span>
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span className="flex min-w-0 items-baseline gap-3">
                    <span className="truncate whitespace-nowrap text-[clamp(1.2rem,1.8vw,1.9rem)] font-medium text-fg">
                      {nombreCorto(l.nombre, l.apellido)}
                    </span>
                    {/* El metadato cede espacio antes que el nombre: trunca él primero */}
                    <span className="hidden min-w-0 shrink-[20] truncate font-mono text-[0.78rem] text-fg-tertiary md:inline">
                      {[l.area_nombre, l.linea_codigo].filter(Boolean).join(' · ') || '—'}
                    </span>
                    {primero ? (
                      <span
                        className={`hidden shrink-0 rounded border border-border-strong px-2 py-0.5 ${ETIQUETA} tracking-[0.1em] text-fg-secondary xl:inline`}
                      >
                        LÍDER DEL TURNO
                      </span>
                    ) : null}
                  </span>
                  <span
                    className="text-right text-[clamp(0.95rem,1.15vw,1.25rem)] text-fg"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {fmt.format(l.producido)}
                  </span>
                  <span
                    className="text-right text-[clamp(0.95rem,1.15vw,1.25rem)] text-fg-secondary"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {l.esperado > 0 ? fmt.format(l.esperado) : '—'}
                  </span>
                  <Barra pct={l.cumplimiento_pct} height={9} />
                  <span
                    className="text-right text-[clamp(1.05rem,1.35vw,1.5rem)] font-medium"
                    style={{
                      color: colorPct(l.cumplimiento_pct),
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {l.cumplimiento_pct === null ? '—' : `${Math.round(l.cumplimiento_pct)}%`}
                  </span>
                </div>
              );
            })}
          </div>
          {restantes > 0 ? (
            <p className="pt-2 text-right text-sm text-fg-tertiary">
              +{restantes} líder{restantes === 1 ? '' : 'es'} más en el turno
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}
